import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import { getWallet, updateWallet } from "../../../services/wallet.service";
import { useSelector } from "react-redux";

export default function WalletUpdate({ existingTransaction }) {
  const [isLoading2, setIsLoading2] = useState(false);
  const { token } = useSelector((state) => state?.auth);
  const [retailerWallet, setRetailerWallet] = useState(null);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const retailerId = params.get("id");
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
    watch,
  } = useForm();

  const fetchWallet = async () => {
    try {
      const res = await getWallet(token, retailerId);

      if (res && res.status >= 200 && res.status < 300) {
        // console.log(res.data);

        setRetailerWallet(res?.data);
      } else {
        Swal.fire(
          "Error",
          "Failed to fetch wallet details. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching wallet details:", error);
      Swal.fire(
        "Error",
        "An error occurred while fetching the wallet. Please try again later.",
        "error"
      );
    }
  };

  useEffect(() => {
    if (retailerId) {
      fetchWallet();
    }
  }, [retailerId]);

  const onSubmit = async (item) => {
    setIsLoading2(true);

    console.log(item);

    const data = {
      retailer_id: retailerId,
      credit_wallet: parseFloat(item.amount),
      transaction_type: item.transactionType,
      note: item.note,
    };

    // console.log(data);

    try {
      const res = await updateWallet(token, data);
      // console.log(res.data);

      if (res && res.status >= 200 && res.status < 300) {
        Swal.fire({
          title: "Updated!",
          text: `Wallet for retailer ${retailerWallet?.retailer_name} has been updated successfully.`,
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          // navigate("/retailer/credit-wallet");
          fetchWallet();
        });
      } else if (res && res.status >= 400 && res.status < 500) {
        Swal.fire(
          "Error",
          res?.data?.error ||
            "Invalid input. Please check the transaction details.",
          "error"
        );
      } else {
        Swal.fire(
          "Error",
          res?.data?.error || "An unexpected error occurred. Please try again later.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error during updating wallet:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error || "A network or server error occurred. Please try again later.",
        "error"
      );
    } finally {
      setIsLoading2(false);
    }
  };

  return (
    <div className="content-body">
      <div className="container-fluid">
        <div className="row">
          <div className="col-xl-12 col-lg-12">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title d-flex">
                  Update Wallet{" "}
                  <h5 className="mt-1 ms-3">
                    (Retailer: {retailerWallet?.retailer_name})
                  </h5>
                </h4>

                <Link to="/retailer/credit-wallet">
                  <img
                    src="/assets/images/kidskart/icon/back-button.png"
                    alt="Back"
                  />
                </Link>
              </div>
              <div className="card-body py-3">
                <div className="basic-form">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-4">
                          <label className="form-label">
                            Current Wallet Balance
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={`₹${retailerWallet?.total_wallet_amount}`}
                            style={{
                              backgroundColor: "#f8f9fa",
                            }}
                            disabled
                          />
                        </div>

                        {/* Dropdown to select Add or Reduce */}
                        <div className="mb-4">
                          <div className="d-flex">
                            <label className="form-label">
                              Transaction Type
                            </label>
                            <i className="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                          </div>
                          <Select
                            options={[
                              { value: "add", label: "Add" },
                              { value: "reduced", label: "Reduce" },
                            ]}
                            {...register("transactionType", {
                              required: "Transaction type is required",
                            })}
                            onChange={(selectedOption) => {
                              setValue(
                                "transactionType",
                                selectedOption?.value
                              );
                              clearErrors("transactionType");
                            }}
                            placeholder="Select Transaction Type"
                            isClearable
                            value={[
                              { value: "add", label: "Add" },
                              { value: "reduced", label: "Reduce" },
                            ].find(
                              (option) =>
                                option.value === watch("transactionType")
                            )}
                          />
                          {errors.transactionType && (
                            <p className="error">
                              {errors.transactionType.message}
                            </p>
                          )}
                        </div>

                        {/* Amount Field */}
                        <div className="mb-4">
                          <div className="d-flex">
                            <label className="form-label">
                              {watch("transactionType") === "add"
                                ? "Add Wallet Balance"
                                : watch("transactionType") === "reduced"
                                ? "Reduce Wallet Balance"
                                : "Wallet Balance"}
                            </label>
                            <i className="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                          </div>
                          <input
                            type="number"
                            className="form-control"
                            placeholder={
                              watch("transactionType") &&
                              `Enter amount to ${watch("transactionType")}`
                            }
                            {...register("amount", {
                              required: "Amount is required",
                              min: {
                                value: 1,
                                message: "Amount must be greater than zero",
                              },
                            })}
                          />
                          {errors.amount && (
                            <p className="error">{errors.amount.message}</p>
                          )}
                        </div>

                        <div className="mb-4">
                          <label className="form-label">Note</label>
                          <textarea
                            type="number"
                            className="form-control"
                            placeholder="Enter Note"
                            {...register("note")}
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        {retailerWallet?.user_wallets.length > 0 && (
                          <div className="mb-4">
                            <h5>Wallet Transaction History</h5>
                            <div
                              className="transaction-history"
                              style={{ maxHeight: "397px", overflow: "auto" }}
                            >
                              {retailerWallet?.user_wallets.length > 0 &&
                                retailerWallet?.user_wallets?.map(
                                  (transaction, index) => (
                                    <div
                                      key={index}
                                      className={`transaction-item ${
                                        transaction.transaction_type === "add"
                                          ? "add"
                                          : "reduce"
                                      }`}
                                    >
                                      <p className="m-0">
                                        {transaction.transaction_type === "add"
                                          ? `Added ₹${transaction.amount.toLocaleString()} : ${new Date(
                                              transaction.transaction_date
                                            )
                                              .toLocaleDateString("en-GB", {
                                                day: "2-digit",
                                                month: "long",
                                                year: "numeric",
                                              })
                                              .replace(" ", " ")
                                              .replace(/(\w+ \w+)/, "$1,")} ${
                                              transaction?.note
                                                ? "(" + transaction.note + ")"
                                                : ""
                                            }`
                                          : `Reduced ₹${transaction.amount.toLocaleString()} : ${new Date(
                                              transaction.transaction_date
                                            )
                                              .toLocaleDateString("en-GB", {
                                                day: "2-digit",
                                                month: "long",
                                                year: "numeric",
                                              })
                                              .replace(" ", " ")
                                              .replace(/(\w+ \w+)/, "$1,")} ${
                                              transaction?.note
                                                ? "(" + transaction.note + ")"
                                                : ""
                                            }`}
                                      </p>
                                    </div>
                                  )
                                )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Submit Button */}
                    {!isLoading2 ? (
                      <div className="col-sm-4">
                        <button type="submit" className="btn btn-primary w-100">
                          UPDATE WALLET
                        </button>
                      </div>
                    ) : (
                      <div className="col-sm-4">
                        <button
                          type="button"
                          className="btn btn-primary w-100"
                          disabled
                        >
                          PROCESSING...
                        </button>
                        <div
                          className="spinner-border2 text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
